/**
 * Reset step 2 — the six-digit code. "Resend" counts down through the server's cooldown instead of
 * inviting a refusal, and "Use a different email" goes back, for the address that was mistyped.
 *
 * The code field does NOT auto-submit on the sixth digit: that is a change of context on input
 * (WCAG 3.2.2), and it would spend one of the router's five attempts on every typo.
 *
 * Resend is judged by the router's CAPTCHA gate, like the first request for a code, and a Turnstile token is single-use
 * (the first request spent the email step's). So the modal's widget renders here too, once Resend is available, the
 * resend carries its fresh token (PLANNER-PALM-UAT-2026-11-03-001 U1: with no widget on this step the resend sent no
 * token and a deployed server refused it), and Resend is held until the widget has issued one (finish gate 7c).
 *
 * @module @puffin-core/login-shell/components/passwordReset/ResetCodeStep
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ResetErrorAlert } from './ResetErrorAlert.jsx';
import { ResetStepIndicator } from './ResetStepIndicator.jsx';

/**
 * @param {Object} props
 * @param {Object} props.flow - `usePasswordResetFlow`'s return
 * @param {Function} props.translate
 * @param {{token: (string|null), blocked: boolean, reset: Function, field: React.ReactNode}} props.captcha - the
 *   modal's ONE widget, shown once Resend is available; Resend is held while it has no required token
 * @returns {React.JSX.Element}
 */
export function ResetCodeStep({ flow, translate, captcha }) {
  const { state, secondsUntilResend } = flow;
  const codeRef = useRef(null);
  useEffect(() => {
    codeRef.current.focus();
  }, []);

  const coolingDown = secondsUntilResend > 0;

  return (
    <form
      onSubmit={flow.handleVerifyCode}
      aria-label={translate('modal.passwordReset.form.code')}
      noValidate
    >
      <ResetStepIndicator current={2} total={3} translate={translate} />
      <p className="text text-sm text-muted mb-4">
        {translate('modal.passwordReset.multiStep.codeBody', { email: state.email.trim() })}
      </p>
      <ResetErrorAlert error={state.error} translate={translate} />
      <div className="form-group">
        <label className="form-label" htmlFor="reset-code">
          {translate('modal.passwordReset.multiStep.codeLabel')}
        </label>
        <input
          ref={codeRef}
          className="form-input"
          id="reset-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          pattern="\d{6}"
          value={state.code}
          onChange={(e) => flow.setField('code', e.target.value.replace(/\D/g, ''))}
          aria-required="true"
          autoComplete="one-time-code"
          disabled={state.isLoading}
          data-testid="reset-code-input"
        />
      </div>
      <button
        type="submit"
        className="btn btn-primary btn-lg w-full"
        disabled={state.code.length !== 6 || state.isLoading}
        aria-busy={state.isLoading ? 'true' : 'false'}
        data-testid="reset-code-submit"
      >
        {state.isLoading
          ? translate('modal.passwordReset.submit.loading')
          : translate('modal.passwordReset.multiStep.codeSubmit')}
      </button>
      {!coolingDown && (
        <div className="form-group mt-sm" data-testid="reset-code-captcha-slot">
          {captcha.field}
        </div>
      )}
      <div className="flex-row justify-between gap-sm mt-sm">
        <button
          type="button"
          className="btn btn-link text-sm"
          onClick={flow.handleBackToEmail}
          disabled={state.isLoading}
          data-testid="reset-code-back"
        >
          {translate('modal.passwordReset.multiStep.differentEmail')}
        </button>
        <button
          type="button"
          className="btn btn-link text-sm"
          onClick={() => flow.handleResend(captcha)}
          disabled={coolingDown || state.isLoading || captcha.blocked}
          data-testid="reset-code-resend"
        >
          {coolingDown
            ? translate('modal.passwordReset.multiStep.resendIn', { seconds: secondsUntilResend })
            : translate('modal.passwordReset.multiStep.resendCode')}
        </button>
      </div>
    </form>
  );
}

ResetCodeStep.propTypes = {
  flow: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  captcha: PropTypes.shape({
    token: PropTypes.string,
    blocked: PropTypes.bool.isRequired,
    reset: PropTypes.func.isRequired,
    field: PropTypes.node.isRequired,
  }).isRequired,
};

export default ResetCodeStep;
